import { apiPath } from '../../constants';
import { fetchCategoryOverview } from '../CategoryOverview';
import { fetchCompetitionCategories } from '../CompetitionCategory';
import {
    axios,
    apiRequestOptions,
    errorRequestHandler,
    transformHttpResponse,
} from 'utils';

export const SCORE_ENTRY_REQUEST = 'POST_SCORE_ENTRY_REQUEST';
export const SCORE_ENTRY_FAILURE = 'POST_SCORE_ENTRY_FAILURE';
export const SCORE_ENTRY_SUCCESS = 'POST_SCORE_ENTRY_SUCCESS';

export const SCORE_TIE_BREAK_REQUEST = 'POST_SCORE_TIE_BREAK_REQUEST';
export const SCORE_TIE_BREAK_FAILURE = 'POST_SCORE_TIE_BREAK_FAILURE';
export const SCORE_TIE_BREAK_SUCCESS = 'POST_SCORE_TIE_BREAK_SUCCESS';

export const SCORE_ROUND_COMPLETE_REQUEST = 'POST_SCORE_ROUND_COMPLETE_REQUEST';
export const SCORE_ROUND_COMPLETE_FAILURE = 'POST_SCORE_ROUND_COMPLETE_FAILURE';
export const SCORE_ROUND_COMPLETE_SUCCESS = 'POST_SCORE_ROUND_COMPLETE_SUCCESS';

export const SCORE_ROUND_REOPEN_REQUEST = 'POST_SCORE_ROUND_REOPEN_REQUEST';
export const SCORE_ROUND_REOPEN_FAILURE = 'POST_SCORE_ROUND_REOPEN_FAILURE';
export const SCORE_ROUND_REOPEN_SUCCESS = 'POST_SCORE_ROUND_REOPEN_SUCCESS';

export const POINTS_STAGE_RESET_REQUEST = 'POST_POINTS_STAGE_RESET_REQUEST';
export const POINTS_STAGE_RESET_FAILURE = 'POST_POINTS_STAGE_RESET_FAILURE';
export const POINTS_STAGE_RESET_SUCCESS = 'POST_POINTS_STAGE_RESET_SUCCESS';

/**
 * Score-judged rounds of the Competition Execution Engine. Every mutation
 * answers with the FULL category rows (backend = single source of truth) —
 * the results reducer swaps the category wholesale and the overview cache
 * drops, so lifecycle status / rankings always re-derive server-side.
 * Thunks resolve with the rows (undefined on failure) for optimistic-free
 * chaining in the panel.
 */
export const enterScore = (resultId, { score = null, dns = false, dnsReason = null }, competition, category) => {
    return function(dispatch) {
        dispatch({ type: SCORE_ENTRY_REQUEST });
        return axios(apiRequestOptions(apiPath.COMPETITION_RESULT.SCORE, { score, dns, dnsReason }, { params: { id: resultId } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: SCORE_ENTRY_SUCCESS, results: response.data, competition, category });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: SCORE_ENTRY_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

export const setScoreTieBreak = (resultId, order, competition, category) => {
    return function(dispatch) {
        dispatch({ type: SCORE_TIE_BREAK_REQUEST });
        return axios(apiRequestOptions(apiPath.COMPETITION_RESULT.SCORE_TIE_BREAK, { order }, { params: { id: resultId } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: SCORE_TIE_BREAK_SUCCESS, results: response.data, competition, category });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: SCORE_TIE_BREAK_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

export const completeScoreRound = (competition, category, round) => {
    return function(dispatch) {
        dispatch({ type: SCORE_ROUND_COMPLETE_REQUEST });
        return axios(apiRequestOptions(apiPath.COMPETITION_RESULT.ROUND_COMPLETE, null, { params: { competition, category, round } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: SCORE_ROUND_COMPLETE_SUCCESS, results: response.data, competition, category, round });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: SCORE_ROUND_COMPLETE_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

export const reopenScoreRound = (competition, category, round) => {
    return function(dispatch) {
        dispatch({ type: SCORE_ROUND_REOPEN_REQUEST });
        return axios(apiRequestOptions(apiPath.COMPETITION_RESULT.ROUND_REOPEN, null, { params: { competition, category, round } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: SCORE_ROUND_REOPEN_SUCCESS, results: response.data, competition, category, round });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: SCORE_ROUND_REOPEN_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

/**
 * Bugfix: clears the Points stage (qualified athletes, scores, rankings) and
 * unlocks the Flag bracket it fed — see CategoryWorkflowService::
 * canEditBracket(). SUPER_ADMIN only (the backend re-enforces this).
 */
export const resetPointsStage = (competition, category) => {
    return function(dispatch) {
        dispatch({ type: POINTS_STAGE_RESET_REQUEST });
        return axios(apiRequestOptions(apiPath.COMPETITION_RESULT.POINTS_STAGE_RESET, null, { params: { competition, category } }))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: POINTS_STAGE_RESET_SUCCESS, results: response.data, competition, category });
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: POINTS_STAGE_RESET_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};

export const OFFICIAL_RESULTS_CONFIRM_REQUEST = 'OFFICIAL_RESULTS_CONFIRM_REQUEST';
export const OFFICIAL_RESULTS_CONFIRM_SUCCESS = 'OFFICIAL_RESULTS_CONFIRM_SUCCESS';
export const OFFICIAL_RESULTS_CONFIRM_FAILURE = 'OFFICIAL_RESULTS_CONFIRM_FAILURE';

/**
 * Publishes the official protocol of a category.
 *
 * The backend re-runs every validation before writing, so a stale page can
 * never publish an incomplete category; a 422 carries the reasons back.
 */
export const confirmOfficialResults = (competition, category) => {
    return function(dispatch) {
        dispatch({ type: OFFICIAL_RESULTS_CONFIRM_REQUEST });
        return axios(apiRequestOptions(
            apiPath.COMPETITION_RESULT.OFFICIAL_RESULTS_CONFIRM,
            null,
            { params: { competition, category } },
        ))
            .then(transformHttpResponse)
            .then(response => {
                dispatch({ type: OFFICIAL_RESULTS_CONFIRM_SUCCESS, competition, category });
                // The lifecycle, the ranking status and every read-only gate
                // are derived server-side, so the page re-reads them rather
                // than guessing what confirmation changed.
                dispatch(fetchCategoryOverview(competition, category));
                /*
                 * …AND THE CATEGORY LIST BESIDE IT.
                 *
                 * The overview feeds the workspace on the right. The list on
                 * the left reads `competitionCategories`, which nothing told:
                 * so the green tick did not appear, and a «Не підтверджені»
                 * filter went on showing a category that had just been
                 * confirmed, until something else happened to refetch
                 * (1 Oct 2026).
                 *
                 * Re-read rather than flipped locally, for the same reason as
                 * the line above: `closed` and `resultsAwaitingConfirmation`
                 * are the server's answers, and a second definition of what
                 * confirmation changes is how the two drift apart.
                 */
                dispatch(fetchCompetitionCategories(competition));
                return response.data;
            })
            .catch(error => transformHttpResponse(error.response))
            .catch(error => {
                dispatch({ type: OFFICIAL_RESULTS_CONFIRM_FAILURE, error });
                dispatch(errorRequestHandler(error));
            });
    }
};
